import React, { useState, useEffect, useRef, memo } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '@/components/layout/Sidebar';

interface ClientLayoutProps {
  children: React.ReactNode;
  title?: string; // 모바일 헤더에 표시할 타이틀
}

// 모바일 환경 감지 헬퍼 함수 (브라우저 API 사용)
const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 1024 || 
    Boolean(navigator.userAgent.match(/Android/i)) || 
    Boolean(navigator.userAgent.match(/iPhone|iPad|iPod/i));
};

// 메모이제이션된 모바일 헤더 컴포넌트
const MobileHeader = memo(({ onToggleSidebar, title }: { onToggleSidebar: () => void; title?: string }) => (
  <div className="lg:hidden bg-white shadow-sm sticky top-0 z-10">
    <div className="flex items-center px-4 py-3 gap-3">
      <button
        onClick={onToggleSidebar}
        className="text-gray-600 hover:text-gray-800 flex-shrink-0"
        aria-label="메뉴 열기"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>
      {title && (
        <h1 className="text-lg font-extrabold text-slate-800 font-['Pretendard'] flex-1 text-center pr-6">
          {title}
        </h1>
      )}
    </div>
  </div>
));

// 메모이제이션된 오버레이 컴포넌트
const Overlay = memo(({ isVisible, onClose }: { isVisible: boolean, onClose: () => void }) => (
  <div
    className="fixed inset-0 bg-black z-20 will-change-opacity"
    style={{ 
      transition: 'opacity 200ms ease-in-out',
      opacity: isVisible ? 0.5 : 0,
      pointerEvents: isVisible ? 'auto' : 'none',
      visibility: isVisible ? 'visible' : 'hidden',
    }}
    onClick={onClose}
  />
));

const ClientLayout: React.FC<ClientLayoutProps> = ({ children, title }) => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileDevice);
  const sidebarInitialized = useRef(false);
  
  // 사이드바 토글 함수 (useCallback 없이도 메모이제이션 컴포넌트에 안전하게 전달)
  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };
  
  // 초기 렌더링 후 사이드바 초기화 완료 표시
  useEffect(() => {
    sidebarInitialized.current = true;
  }, []);
  
  // 디바운스 처리된 윈도우 리사이즈 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    // 초기 확인 (즉시 실행)
    checkMobile();
    
    // 리사이즈 이벤트 핸들러 (강화된 디바운스)
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(checkMobile, 100); // 더 짧은 딜레이
    };
    
    // 최적화된 이벤트 리스너 (passive: true)
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // 페이지 변경 시 모바일에서 사이드바 닫기
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // 로딩 실패 백업 처리 (1초 후 강제 초기화)
  useEffect(() => {
    if (!sidebarInitialized.current) {
      const timer = setTimeout(() => {
        setIsMobile(isMobileDevice());
        sidebarInitialized.current = true;
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* 메모이제이션된 오버레이 */}
      <Overlay isVisible={isMobile && isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      
      {/* 사이드바 - 로딩 보장을 위한 조건부 렌더링 제거 */}
      <Sidebar 
        isOpen={isSidebarOpen} 
        onClose={() => setIsSidebarOpen(false)}
        isMobile={isMobile}
        location={location}
      />
      
      {/* 메인 컨텐츠 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 메모이제이션된 모바일 헤더 */}
        {isMobile && <MobileHeader onToggleSidebar={toggleSidebar} title={title} />}
        
        {/* 컨텐츠 영역 */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientLayout; 